import * as Sentry from "@sentry/vue";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { onSnapshot, type Unsubscribe } from "firebase/firestore";
import { APP_CONTROL_DOC_ID } from "~/interface/AppControl";
import type {
  AppControlAnnouncement,
  AppControlDoc,
  AppControlStoreDoc,
  BuildChannel,
} from "~/interface/AppControl";

/** updatedAt ของประกาศที่ผู้ใช้กดปิดไปแล้ว แยกตาม channel */
export const ANNOUNCEMENT_DISMISSED_KEY_PREFIX = "announcementDismissed_";

export const announcementDismissedKey = (channel: BuildChannel) =>
  `${ANNOUNCEMENT_DISMISSED_KEY_PREFIX}${channel}`;

// คนที่รอ snapshot แรก (หน้าสต๊อกต้องรู้ค่า flag ก่อนเลือกว่าจะทำงานแบบไหน)
// เก็บนอก state เพราะเป็น resolver ของ Promise ไม่ใช่ข้อมูล
let snapshotWaiters: Array<() => void> = [];

const releaseSnapshotWaiters = () => {
  const waiters = snapshotWaiters;
  snapshotWaiters = [];
  waiters.forEach((resolve) => resolve());
};

/**
 * source of truth ของคำสั่งจาก Firestore — ฟังค่าแล้วเก็บดิบไว้ ไม่ตัดสินอะไรแทนใคร
 *
 *   doc       ← `app_control/{channel}`          { reload, announcement, stock_checkpoint }
 *   serverDoc ← doc เดียวกัน แต่เฉพาะ snapshot ที่ยืนยันจาก server แล้ว (reload ใช้ตัวนี้)
 *   storeDoc  ← `app_control_stores/{storeId}`   { reload } (เฉพาะ snapshot ที่ยืนยันจาก server)
 *
 * ใครอยากทำอะไรเมื่อค่าไหนเปลี่ยน ให้ไปเขียนเป็น pinia plugin ที่ watch สโตร์นี้
 * (เช่น reload อยู่ที่ modules/core/plugins/appReload.plugin.ts) ไม่ต้องมาเพิ่มในสโตร์
 * สโตร์นี้จะได้ไม่บวมตามจำนวนฟีเจอร์
 *
 * ทุกอย่างในนี้ต้องทนกับกรณี Firestore ต่อไม่ติด: ไม่มี snapshot = doc เป็น null
 * แอปทำงานปกติ ไม่มี popup error
 */
export const useAppControlStore = defineStore("appControl", () => {
  const channel = ref<BuildChannel>("development");

  // doc ล่าสุดจาก app_control/{channel}
  const doc = ref<AppControlDoc | null>(null);
  // เหมือน doc แต่ไม่รับค่าจากแคช — undefined = ยังไม่ได้คำตอบจาก server เลย
  // reload จับ hash ตัวแรกเป็นค่าตั้งต้น ถ้าได้ค่าเก่าจากแคชจะโหลดซ้ำทั้งที่ bundle ใหม่อยู่แล้ว
  const serverDoc = ref<AppControlDoc | null | undefined>(undefined);

  // ร้านที่ล็อกอินอยู่ ("" = ยังไม่รู้ / logout)
  const storeId = ref("");

  // doc ล่าสุดจาก app_control_stores/{storeId} ของร้าน storeDocStoreId
  // เก็บ storeId คู่กันไว้ คนที่ใช้ต้องเช็คว่าเป็นของร้านปัจจุบัน (snapshot ร้านเก่าอาจมาช้าหลังสลับร้าน)
  // storeDocStoreId = "" แปลว่ายังไม่ได้ snapshot จาก server ของร้านไหนเลย
  const storeDoc = ref<AppControlStoreDoc | null>(null);
  const storeDocStoreId = ref("");

  const announcementDismissedAt = ref<string | null>(null);

  // ได้ snapshot แรกของ app_control แล้วหรือยัง (หรือรู้แล้วว่าอ่านไม่ได้)
  // ก่อนหน้านั้นค่า feature flag ยังเชื่อไม่ได้
  const snapshotSettled = ref(false);
  // ได้ snapshot จริงอย่างน้อยหนึ่งครั้ง (ไม่นับกรณีอ่านไม่ได้)
  // ใช้ตัดสินว่า "เห็น flag ปิดจริง" ไม่ใช่แค่เน็ตหลุด
  const snapshotReceived = ref(false);

  /**
   * หน้าสต๊อกคำนวณในเครื่อง (local mode) — true เฉพาะเมื่อค่าเป็น true จริงเท่านั้น
   * ไม่มี doc / ไม่มีฟิลด์ / อ่าน Firestore ไม่ได้ = false = ทำงานแบบเดิม
   */
  const stockCheckpointEnabled = computed(() => doc.value?.stock_checkpoint === true);

  /** ประกาศที่ "เปิดใช้งาน" อยู่ — ยังไม่ได้กรองช่วงเวลา/การกดปิด */
  const announcement = computed<AppControlAnnouncement | null>(() => {
    const value = doc.value?.announcement;
    if (!value || value.enabled !== true) return null;
    return value;
  });

  let started = false;

  /**
   * เริ่มฟัง Firestore — เรียกครั้งเดียวตอนบูต (appControl.client.ts)
   *
   * channel มาจากค่าที่ฝังตอน build (useBuildChannel) ไม่ใช่ hostname
   * app_control ไม่ผูกกับร้าน subscribe ได้ทันที ส่วน app_control_stores รอ storeId
   */
  function start(nextChannel: BuildChannel) {
    if (started) return;
    started = true;

    channel.value = nextChannel;
    announcementDismissedAt.value = safeGet(announcementDismissedKey(nextChannel));

    listenStoreDoc();

    // includeMetadataChanges เพื่อให้ได้ event ตอนแคช → server แม้ข้อมูลไม่เปลี่ยน
    onSnapshot(
      useFirebaseFirestore().appControlDocRef(APP_CONTROL_DOC_ID[nextChannel]),
      { includeMetadataChanges: true },
      (snapshot) => {
        const data = (snapshot.data() as AppControlDoc | undefined) ?? null;
        doc.value = data;
        if (!snapshot.metadata.fromCache) serverDoc.value = data;
        snapshotSettled.value = true;
        snapshotReceived.value = true;
        releaseSnapshotWaiters();
      },
      (error) => {
        // อ่าน doc ไม่ได้ (เน็ตหลุด / rules ไม่ให้อ่าน / ยังไม่ได้สร้าง collection)
        // ไม่ใช่เรื่องที่ผู้ใช้ต้องรู้ — feature flag ทุกตัวถือว่าปิด (หน้าสต๊อกทำงานแบบเดิม)
        console.warn("[appControl] อ่าน app_control ไม่ได้ ข้ามไปก่อน", error);
        snapshotSettled.value = true;
        releaseSnapshotWaiters();
      }
    );
  }

  /**
   * ฟัง `app_control_stores/{storeId}` ตามร้านที่ล็อกอินอยู่
   *
   * ห้ามฟัง root doc `stores/{storeId}` — doc นั้นถูกเขียนถี่ (สถานะร้าน ฯลฯ)
   * ทุกการเขียน = 1 read ต่อเครื่องที่ฟัง คำสั่งเฉพาะร้านจึงแยกไปอยู่ใน collection ของตัวเอง
   */
  function listenStoreDoc() {
    // shop store ยังไม่ถูกสร้างตอนนี้ก็ไม่เป็นไร pinia สร้างให้ตอนเรียก
    const shopStore = useShopStore();
    const { appControlStoreDocRef } = useFirebaseFirestore();
    let unsubscribe: Unsubscribe | null = null;

    watch(
      () => shopStore.storeId,
      (value) => {
        const nextStoreId = value?.trim() || "";
        storeId.value = nextStoreId;

        // สลับร้าน / logout ต้องเลิกฟังร้านเดิมก่อน ไม่งั้นคำสั่งของร้านเก่ายังมาถึงเครื่องนี้ได้
        unsubscribe?.();
        unsubscribe = null;
        storeDoc.value = null;
        storeDocStoreId.value = "";
        if (!nextStoreId) return;

        // includeMetadataChanges เพื่อให้ได้ event ตอนแคช → server แม้ข้อมูลไม่เปลี่ยน
        // แล้วเก็บเฉพาะ snapshot ของ server — ค่าจากแคชอาจเก่า คนที่ใช้ (เช่น reload)
        // จะเข้าใจผิดว่าเป็นค่าล่าสุด
        unsubscribe = onSnapshot(
          appControlStoreDocRef(nextStoreId),
          { includeMetadataChanges: true },
          (snapshot) => {
            if (snapshot.metadata.fromCache) return;
            // ยังไม่มี doc ของร้านนี้ (ไม่เคยสั่ง) = เก็บเป็น {} ให้รู้ว่า "ได้คำตอบแล้ว ว่าง"
            storeDoc.value = (snapshot.data() as AppControlStoreDoc | undefined) ?? {};
            storeDocStoreId.value = nextStoreId;
          },
          (error) => {
            // อ่านไม่ได้ = แค่คำสั่งเฉพาะร้านไม่ถึง แอปทำงานต่อได้ปกติ
            // แต่ listener ที่ error แล้วจะตายถาวรจนกว่า storeId เปลี่ยน — ส่งขึ้น Sentry
            // ให้เห็นว่าร้าน/เครื่องไหนไม่ได้รับคำสั่ง (เช่น rules ของ app_control_stores ยังไม่ deploy)
            console.warn("[appControl] อ่าน app_control_stores/{storeId} ไม่ได้ ข้ามไปก่อน", error);
            try {
              Sentry.captureMessage("[appControl] store reload listener failed", {
                level: "warning",
                extra: { storeId: nextStoreId, code: error.code, source: "store" },
              });
            } catch {
              // Sentry ไม่ได้ init (dev / localhost)
            }
          }
        );
      },
      { immediate: true }
    );
  }

  /**
   * รอ snapshot แรกไม่เกิน timeoutMs แล้วตอบว่าหน้าสต๊อกควรคำนวณในเครื่องไหม
   * ไม่มาภายในเวลา = ถือว่าปิด (ทำงานแบบเดิม) ปลอดภัยกว่าเดา
   * หมดเวลาครั้งเดียวพอ ครั้งต่อไปไม่ต้องรออีก (snapshot มาทีหลัง หน้าสต๊อกสลับโหมดเองผ่าน watch)
   */
  async function waitForStockCheckpointFlag(timeoutMs = 3000): Promise<boolean> {
    if (!snapshotSettled.value) {
      await new Promise<void>((resolve) => {
        const timer = setTimeout(() => {
          snapshotWaiters = snapshotWaiters.filter((fn) => fn !== done);
          snapshotSettled.value = true;
          resolve();
        }, timeoutMs);
        const done = () => {
          clearTimeout(timer);
          resolve();
        };
        snapshotWaiters.push(done);
      });
    }
    return stockCheckpointEnabled.value;
  }

  function dismissAnnouncement() {
    // ประกาศที่ไม่มี updatedAt กดปิดแล้วจะไม่กลับมาจนกว่าจะมีตัวใหม่ที่มี updatedAt
    // จึงบังคับให้ script ใส่ updatedAt ทุกครั้ง (ดู scripts/announce.mjs)
    const marker = announcement.value?.updatedAt || "no-updated-at";

    announcementDismissedAt.value = marker;
    safeSet(announcementDismissedKey(channel.value), marker);
  }

  // ต้องคืน state ทุกตัว — ref ที่ไม่ได้คืน pinia จะไม่เห็น (devtools / $reset / plugin)
  return {
    channel,
    doc,
    serverDoc,
    storeId,
    storeDoc,
    storeDocStoreId,
    announcementDismissedAt,
    snapshotSettled,
    snapshotReceived,
    stockCheckpointEnabled,
    announcement,
    start,
    waitForStockCheckpointFlag,
    dismissAnnouncement,
  };
});

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // private mode / quota เต็ม — จดไม่ได้ก็ต้องยอม แต่ห้ามให้ throw ไปล้มการทำงานอื่น
  }
}
